import {
  useCallback,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { Box, Text, TextField, Menu, SearchIcon } from '@citibox-panel/ui';
import { track } from '@citibox-panel/analytics';
import type { UserSearchHit } from '@citibox-panel/api-client';
import { HEADER_SEARCH_NOTICE_ID, SEARCH_FAILED } from '../../../lib/headerSearchNotice';
import { useErrorNotice } from '../../search-shell/hooks/useErrorNotice';
import { useSearchTabs } from '../../search-shell/hooks/useSearchTabs';
import { usePhoneSearch } from '../hooks/usePhoneSearch';
import { detectPhone } from '../lib/detectPhone';
import { firstActiveRecipient } from '../../../lib/firstActiveRecipient';

export interface PhoneSearchProps {
  search: (phone: string) => Promise<UserSearchHit[]>;
  // The host focuses this input on ⌘K / "+" / empty-state action (shell seam).
  inputRef?: Ref<HTMLInputElement>;
  // Optional control rendered AS the field's end adornment. Unset, the field has none.
  trailing?: ReactNode;
}

// The wording of the one dead end this mode words itself. It NAMES WHAT WAS NOT FOUND — the
// «Usuario» — where the tracking mode's sentence names its window instead: what a header search
// cannot find is the whole answer, so each mode says the part of it that is its own. It deliberately
// drops the old «Prueba con otro teléfono.» hint: a notice is one line, and the next move is the
// field the agent is already in.
// TODO: i18n
const NOT_FOUND = 'No hemos podido encontrar al usuario que buscas';

type SearchResult = 'found' | 'not_found' | 'multiple';

const resultOf = (count: number): SearchResult =>
  count === 0 ? 'not_found' : count === 1 ? 'found' : 'multiple';

export function PhoneSearch({ search, inputRef, trailing }: PhoneSearchProps) {
  const { openSearch } = useSearchTabs();
  const { notify, dismiss } = useErrorNotice();
  const { submit, status } = usePhoneSearch(search);
  const [value, setValue] = useState('');

  // The dropdown anchors to the input itself, so forward the node both to the
  // anchor ref and to the caller's inputRef (which focuses it on ⌘K).
  const anchorRef = useRef<HTMLInputElement | null>(null);
  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      anchorRef.current = node;
      if (typeof inputRef === 'function') inputRef(node);
      else if (inputRef) inputRef.current = node;
    },
    [inputRef],
  );

  const open = (winner: UserSearchHit, searchedPhone: string): void => {
    // An anonymized «Usuario» ships no phone — its PII was nulled — and a tab keyed by
    // the empty string can never refetch its account. The term the search resolved the
    // hit by is the key that stays resolvable, so it stands in for the missing phone.
    const tabPhone = winner.phone !== '' ? winner.phone : searchedPhone;
    // A nameless «Usuario» (carrier pre-registration) opens with no label — an
    // empty-string label would render an empty tab title instead of the header's
    // fallback to the phone.
    openSearch(tabPhone, winner.name || undefined);
    setValue('');
    dismiss(HEADER_SEARCH_NOTICE_ID);
  };

  const runSearch = async (): Promise<void> => {
    // Cleared before asking, so a retry starts from a header with nothing on it.
    dismiss(HEADER_SEARCH_NOTICE_ID);
    let results: UserSearchHit[] | null;
    try {
      results = await submit(value);
    } catch {
      // The API call failed: report it rather than acting on no hits or leaving the
      // dropdown stuck on "Buscando…". Nothing opens under the field — a breakdown is
      // not a candidate, and the region below is where candidates go.
      notify(HEADER_SEARCH_NOTICE_ID, SEARCH_FAILED);
      return;
    }
    if (results === null) return; // not a phone — other input types are a later batch
    track('search_submitted', { kind: 'phone', result: resultOf(results.length) });
    if (results.length === 0) {
      notify(HEADER_SEARCH_NOTICE_ID, NOT_FOUND);
      return;
    }
    if (results.length > 1) {
      // Anomaly: one phone resolved to several «Usuarios». Record it with ids only —
      // no «Usuario» PII in logs — so analytics' `result:'multiple'` can be corroborated.
      console.warn('[phone-search] multipleRecipients', {
        count: results.length,
        ids: results.map((hit) => hit.id),
      });
    }
    const winner = firstActiveRecipient(results);
    // `submit` only resolves hits for input that detects as a phone, so the detection
    // repeated here cannot be null; the fallback exists to satisfy the type.
    if (winner) open(winner, detectPhone(value) ?? winner.phone);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void runSearch();
    }
  };

  // A non-empty input that is not a valid international phone shows an inline hint and
  // never searches (detectPhone gates the submit), nudging the agent to add the prefix.
  const invalidPhone = value.trim().length > 0 && detectPhone(value) === null;
  const isSearching = status === 'searching';

  return (
    // `width` as well as the field's own `fullWidth`: as a flex item in the header's slot this box
    // shrinks to its content, so the field would stop short of the slot's edge. `relative` is what
    // lets the format hint below hang under it without pushing the chrome down.
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        aria-label="Buscar por teléfono"
        // TODO: i18n — copy en ES hasta Fase 1.
        placeholder="Buscar por teléfono…"
        value={value}
        onChange={setValue}
        onKeyDown={onKeyDown}
        ref={setInputRef}
        variant="search"
        fullWidth
        startAdornment={<SearchIcon fontSize="small" />}
        // No ⌘K hint: it only ever rendered in this mode, so the field visibly changed shape when
        // the agent switched search type. The shortcut is untouched — the shell owns that listener.
        {...(trailing !== undefined && trailing !== null ? { endAdornment: trailing } : {})}
      />
      {invalidPhone && (
        // Absolute, so a mistyped phone cannot stretch the header: the hint floats over the content
        // below instead of pushing the chrome down. Its positioning parent is the wrapper above.
        //
        // The hint reads as belonging to the field it corrects, so it sits flush against its bottom
        // edge: the zero padding is written and not omitted, and the negative margin cancels the
        // caption's half-leading — 12px of type on a 1.66 line leaves ~4px of dead air above the
        // capitals, which is line-height, not spacing, and would otherwise read as a gap nobody
        // chose. Pad this and it starts reading as a line of the page instead of a note on a control.
        <Box sx={{ position: 'absolute', top: '100%', left: 0, px: 1.5, pt: 0, mt: -0.5 }}>
          {/* TODO: i18n */}
          <Text variant="caption" color="secondary">
            Introduce el número con prefijo internacional (p. ej. +34…, +44…)
          </Text>
        </Box>
      )}
      {/* Only ever the loading row: every ENDING of this search is elsewhere — a match opens a tab,
          and nothing found or a failed read float as a notice (ADR 0021). So there is no candidate
          list here and no empty state either, which is also what the tracking mode does. */}
      <Menu
        anchorEl={anchorRef.current}
        open={isSearching}
        // Nothing to dismiss: the row's lifetime IS the read's, and a spinner the agent can close
        // while the request is still in flight would be a lie about what is happening.
        onClose={() => undefined}
      >
        <Box sx={{ px: 2, py: 1 }}>
          {/* TODO: i18n */}
          <Text variant="body2" color="secondary">
            Buscando…
          </Text>
        </Box>
      </Menu>
    </Box>
  );
}
